import React, { Fragment, useState, useEffect } from 'react'
import { Row, Col, Dropdown, Button, Image } from 'react-bootstrap'
import { gql, useQuery, useLazyQuery } from '@apollo/client'
import client from '../utils/apollo-client'
import { InView } from 'react-intersection-observer'
import ScrollToTop from 'react-scroll-to-top'
import Card from '../components/common/Card'
import CustomToggle from '../layouts/components/CustomToggle'
import { logos } from '../utils/platform-logos'
import FeedCard from '../components/feed/FeedCard'
import Masonry from '../components/common/Masonry'
import ResponsiveMasonry from '../components/common/ResponsiveMasonry'
import { CORE_FEED_FIELDS } from '../graphql/feed-fragments'
import _ from 'lodash'
import { UsersIcon } from '../components/icons'
import { CubeLoader } from '../layouts/components/CubeLoader'

const GET_PLATFORMS = gql`
    query Platforms($options: PlatformOptions, $where: PlatformWhere) {
        platforms(options: $options, where: $where) {
            id
            name
            type
        }
    }
`

const GET_CAMPAIGN = gql`
    query Campaigns {
        campaigns {
            categories
        }
    }
`

const GET_CATEGORIES = gql`
    query Categories($where: CategoryWhere) {
        categories(where: $where) {
            id
            name
        }
    }
`

const GET_FEEDS = gql`
    ${CORE_FEED_FIELDS}
    query Feeds($where: FeedWhere, $options: FeedOptions) {
        feeds(where: $where, options: $options) {
            ...CoreFeedFields
            comments {
                ...CoreFeedFields
            }
            keywords {
                id
                value
            }
        }
    }
`

const Communities = () => {
    const [platform, setPlatform] = useState(null)
    const [audiences, setAudiences] = useState(null)
    const [category, setCategory] = useState(null)
    const [heights, setHeights] = useState({})
    const [isFullyLoaded, setFullyLoaded] = useState(false)

    const platforms = useQuery(GET_PLATFORMS, {
        client,
        variables: { options: { sort: [{ order: 'ASC' }] }, where: { status: 'ACTIVE' } },
        onCompleted: () => {
            setPlatform(platforms.data.platforms[0])
            getFeeds({
                variables: {
                    where: { platform: { id: platforms.data.platforms[0].id }, type: null },
                    options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
                },
            })
        },
    })

    const [getCategories, categories] = useLazyQuery(GET_CATEGORIES)

    const campaign = useQuery(GET_CAMPAIGN, {
        onCompleted: () => {
            const campaignCategories = campaign.data.campaigns[0].categories
                ? JSON.parse(campaign.data.campaigns[0].categories)
                : []
            const aliases = _.map(campaignCategories, (category) => category.toLowerCase())
            getCategories({
                variables: { where: { alias_IN: aliases } },
            })
        },
    })

    const [getFeeds, feeds] = useLazyQuery(GET_FEEDS)

    const onChangePlatform = (platform) => {
        setFullyLoaded(false)
        setPlatform(platform)
        getFeeds({
            variables: {
                where: {
                    platform: {
                        id: platform?.id,
                    },
                    type: null,
                    profile: {
                        categories_SINGLE: {
                            id: category?.id,
                        },
                    },
                },
                options: {
                    sort: [
                        {
                            postedAt: 'DESC',
                        },
                    ],
                    limit: 18,
                    offset: 0,
                },
            },
        })
    }

    const onChangeCategory = (category) => {
        setFullyLoaded(false)
        setCategory(category)
        getFeeds({
            variables: {
                where: {
                    platform: {
                        id: platform?.id,
                    },
                    type: null,
                    profile: {
                        categories_SINGLE: {
                            id: category?.id,
                        },
                    },
                },
                options: {
                    sort: [
                        {
                            postedAt: 'DESC',
                        },
                    ],
                    limit: 18,
                    offset: 0,
                },
            },
        })
    }

    const onLoadMore = async () => {
        const result = await feeds.fetchMore({
            variables: { options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: feeds.data?.feeds.length } },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev
                return Object.assign({}, prev, {
                    feeds: [...prev.feeds, ...fetchMoreResult.feeds],
                })
            },
        })
        setFullyLoaded(!result.data.feeds.length)
    }

    const handleHeightChange = (index, height) => {
        setHeights((prevState) => {
            return {
                ...prevState,
                [index]: height,
            }
        })
    }

    // Show categories sorted by ratio and if ratio > 5
    useEffect(() => {
        if (categories?.data?.categories && !categories.loading) {
            const array = _.map(categories.data.categories, (category) => {
                return {
                    id: category.id,
                    name: category.name,
                    value: category.ratio,
                }
            })

            setAudiences(array)
        }
    }, [categories])

    return (
        <Fragment>
            {platforms.loading || categories.loading ? (
                <CubeLoader fullScreen={true} />
            ) : (
                <Row className="mb-4">
                    <Col lg="12">
                        <Card className="page-card">
                            <Card.Header className="align-items-center flex-wrap page-card-header">
                                <h6 className="mb-0 d-flex align-items-center gap-2">
                                    <i className="icon text-gray-200">
                                        <UsersIcon size={24} color="currentColor" />
                                    </i>
                                    {category ? category.name : 'All Communities'} - {platform?.name}{' '}
                                    <Button variant="sm bg-white social-btn rounded-circle p-1 no-pointer">
                                        <Image
                                            src={logos[platform?.name]}
                                            alt={platform?.name}
                                            className="img-fluid object-contain"
                                            width={20}
                                            height={20}
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </Button>
                                </h6>
                                <div className="d-flex flex-row">
                                    <Dropdown className="me-3">
                                        <Dropdown.Toggle
                                            as={CustomToggle}
                                            href="#"
                                            variant=" text-secondary dropdown-toggle"
                                            id="dropdownMenuButton4"
                                            aria-expanded="false"
                                        >
                                            <span className="text-sm text-blue-300 mb-0">Select Community</span>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu
                                            className="dropdown-menu-end"
                                            aria-labelledby="dropdownMenuButton4"
                                        >
                                            {audiences &&
                                                audiences.map((item) => (
                                                    <li key={item.id}>
                                                        <Dropdown.Item href="#" onClick={() => onChangeCategory(item)}>
                                                            {item.name}
                                                        </Dropdown.Item>
                                                    </li>
                                                ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    <Dropdown>
                                        <Dropdown.Toggle as={CustomToggle} variant="text-secondary dropdown-toggle">
                                            <span className="text-sm text-blue-300 mb-0">Select Platform</span>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu aria-labelledby="dropdownMenuButtonPlatform">
                                            {platforms.data.platforms.map((item) => {
                                                return (
                                                    <li key={item.id}>
                                                        <Dropdown.Item href="#" onClick={() => onChangePlatform(item)}>
                                                            <Button
                                                                variant="sm social-btn rounded-circle no-pointer p-0 me-1 d-inline-flex align-items-center justify-content-center"
                                                                style={{ width: '20px', height: '20px' }}
                                                            >
                                                                <Image
                                                                    src={logos[item.name]}
                                                                    alt={item.name}
                                                                    className="img-fluid object-contain"
                                                                    style={{ width: '14px', height: '14px' }}
                                                                />
                                                            </Button>{' '}
                                                            {item.name}
                                                        </Dropdown.Item>
                                                    </li>
                                                )
                                            })}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {!feeds?.loading && (
                                        <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 900: 3, 1200: 4 }}>
                                            <Masonry gutter={'20px'} heights={heights}>
                                                {feeds.data?.feeds.map((feed) => (
                                                    <FeedCard
                                                        key={feed.id}
                                                        data={feed}
                                                        platform={platform}
                                                        index={feed.id}
                                                        onHeightChange={handleHeightChange}
                                                    />
                                                ))}
                                                <InView
                                                    onChange={(inView) => {
                                                        if (inView) {
                                                            onLoadMore()
                                                        }
                                                    }}
                                                />
                                            </Masonry>
                                        </ResponsiveMasonry>
                                    )}
                                    {!isFullyLoaded && <CubeLoader fullScreen={true} />}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
            <ScrollToTop smooth color="white" className="turbamedia-fab" />
        </Fragment>
    )
}

export default Communities
